import { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	type ResultClickedCallback,
	type ResultClickedCallbackParams,
} from '../../../common/constants/types';
import {
	type AdvancedSearchResultInteractedMetadata,
	onAdvancedSearchResultLinkClicked,
} from '../../../common/utils/events/advanced-search-result-interacted';
import { isIntentDetectionTriggeredForExperiment } from '../../../common/utils/experiments/experiment-exports';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';
import { usePrimaryProduct } from '../../store/bootstrap';

interface FireResultClickedProps {
	metadata: AdvancedSearchResultInteractedMetadata;
	/** The index in which the result was shown to the user */
	position?: number;
	socialSignal?: string;
	isImpersonated?: boolean;
	is3PClickIngestionEnabled?: boolean;
}

/**
 * @returns a callback to fire an event when the result
 * is clicked by the user.
 */
export const useFireResultClicked = ({
	metadata,
	position,
	socialSignal,
	isImpersonated = false,
	is3PClickIngestionEnabled,
}: FireResultClickedProps): ResultClickedCallback => {
	const intl = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const [primaryProduct] = usePrimaryProduct();
	const intentDetectionTriggered = fg('intent_detection_triggered_metrics_enabled')
		? isIntentDetectionTriggeredForExperiment(primaryProduct, intl.locale)
		: undefined;

	return useCallback(
		(params: ResultClickedCallbackParams = {}) => {
			const {
				fromLinksSeeAll,
				sublistPosition,
				provider,
				linkDocId,
				linkDocContentType,
				fanoutProduct,
				fanoutLinkCategory,
				hasRelatedLinks,
				thirdPartyId, // This is different from ID. It's needed to track clicks on results in Act. Platform.
				isAuxClick,
				isSummarizeClick,
				tmpAuxDiagnostics,
			} = params;
			const analyticsPayload = onAdvancedSearchResultLinkClicked({
				metadata,
				position,
				socialSignal,
				commonAttributes: {
					...commonAttributes,
					...getExtensionData(commonAttributes.isCQL),
				},
				nonPrivacySafeAttributes,
				fromLinksSeeAll,
				sublistPosition,
				provider,
				fanoutProduct,
				linkDocId,
				linkDocContentType,
				fanoutLinkCategory,
				isImpersonated,
				hasRelatedLinks,
				is3PClickIngestionEnabled,
				thirdPartyId,
				isAuxClick,
				isSummarizeClick,
				...(intentDetectionTriggered !== undefined ? { intentDetectionTriggered } : {}),
				tmpAuxDiagnostics,
			});

			fireAnalyticsEvent(analyticsPayload);
		},
		[
			fireAnalyticsEvent,
			metadata,
			position,
			socialSignal,
			commonAttributes,
			nonPrivacySafeAttributes,
			isImpersonated,
			is3PClickIngestionEnabled,
			intentDetectionTriggered,
		],
	);
};
